import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { dropDownSelectionStyles, getValueFromResource } from '../../../common/timeLiveFunction';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import fetchTimeliveAPI from '../../../common/fetchTimeliveAPI';
import { ShowToast } from '../../../common/Toastr';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { activateAttendancePolicyAsync, getAttendancePolicyById, updateAttendancePolicyAsync, archiveAttendancePolicyAsync } from './AttendancePolicySlice';
import { KTSVG } from '../../../_metronic/helpers';
import EmployeeAssigned from '../../../assets/image/EmployeesAssigned.svg';
import DepartmentsAssigned from '../../../assets/image/DepartmentsAssigned.svg';
import LocationAssigned from '../../../assets/image/icon-Location.svg';
import LateArrivalThreshold from '../../../assets/image/LateArrivalThreshold.svg';
import EarlyDepartureThreshold from '../../../assets/image/EarlyDepartureThreshold.svg';

import PolicyConflictModal from './AttendancePolicyModalConflict';
import AttendanceGuidelineModal from './AttendanceGuidlineModal';

interface StatusRule {
    id: string;
    order: number;
    status: string;
    evaluationMethod: string;
    operator: string;
    value: string;
    color: string;
}

const AttendancePolicy = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { decimalPlaces, sessionAccountEmployeeId, calendarStartDay, CurrentCultureDateFormat, sessionAccountId } = useAppSelector((state) => state.globalState);

    const { currentPolicy, selectedRecord, policyList } = useAppSelector((state) => state.attendancePolicy);
    const storedObjectData = localStorage.getItem('objAttendancePolicyItem');
    const objectData = storedObjectData ? JSON.parse(storedObjectData) : null;

    const [policyName, setPolicyName] = useState('');
    
    const [policyStatusId, setPolicyStatusId] = useState<number>(2);
    
    const [description, setDescription] = useState('');
    const [effectiveFromDate, setEffectiveFromDate] = useState<Date | null>(new Date());
    const [effectiveToDate, setEffectiveToDate] = useState<Date | null>(null);
    const [scopeType, setScopeType] = useState<'All' | 'Department' | 'Location' | 'Employee'>('All');

    const [departmentsList, setDepartmentsList] = useState<any[]>([]);
    const [locationsList, setLocationsList] = useState<any[]>([]);
    const [employeesList, setEmployeesList] = useState<any[]>([]);
    const [employeesDropdownList, setEmployeesDropdownList] = useState<any[]>([]);

    const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
    const [selectedPolicyEmployee, setSelectedPolicyEmployee] = useState<any>(null);

    const [deptSearch, setDeptSearch] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [employeeLoadCount, setEmployeeLoadCount] = useState(10);
    const [locationLoadCount, setLocationLoadCount] = useState(10);
    const [lateArrivalMinutes, setLateArrivalMinutes] = useState<number>();
    const [earlyDepartureMinutes, setEarlyDepartureMinutes] = useState<number>();
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [policyStatusTypeList, setPolicyStatusTypeList] = useState<any[]>([]);
    const [evaluationMethodsList, setEvaluationMethodsList] = useState<any[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [rules, setRules] = useState<StatusRule[]>([]);

    const [createdDateStr, setCreatedDateStr] = useState<string>('');
    const [creatorName, setCreatorName] = useState<string>('');
    const [modifiedDateStr, setModifiedDateStr] = useState<string>('');
    const [modifiedByName, setModifiedByName] = useState<string>('');

    const dragIndex = React.useRef<number | null>(null);
    const [dragOver, setDragOver] = useState<number | null>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isLoadingMoreLocation, setIsLoadingMoreLocation] = useState(false);
  
    const [policyId, setPolicyId] = useState<any>(objectData || null);
    const isEditMode = Boolean(policyId && !isNaN(policyId));

    const [isAssignmentEditing, setIsAssignmentEditing] = useState(false);
    const isPolicyActive = policyStatusId === 1;
    const isPolicyArchived = policyStatusId === 3;
    const isDisabled = isPolicyActive || isPolicyArchived;
    const [modalOpen, setModalOpen] = useState(false);
    const [conflictData, setConflictData] = useState<any>(null);
    const [guidelineModalOpen, setGuidelineModalOpen] = useState(false);

    const getStatusLabelAndClass = (statusId: number) => {
        switch (statusId) {
            case 1:
                return { label: 'Active', className: 'bg-success-subtle text-success border-success-subtle' };
            case 3:
                return { label: 'Archived', className: 'bg-warning-subtle text-warning border-warning-subtle' };
            case 2:
            default:
                return { label: 'Draft', className: 'bg-secondary text-gray-600 border-gray-300' };
        }
    };

    useEffect(() => {
        if (policyId && !isNaN(policyId)) {
            dispatch(getAttendancePolicyById(policyId));
        }
    }, [policyId, dispatch]);

    const TONE_COLORS: Record<string, string> = {
        'Present': 'success', 'Short Day': 'warning', 'Half Day': 'primary', 'Absent': 'danger', 'New Status': 'info'
    };

    const assignedEmployeesCount = useMemo(() => {
        if (scopeType === 'Employee') return selectedEmployees.length;
        if (scopeType === 'All') return employeesList.length;
        return 0;
    }, [scopeType, selectedEmployees, employeesList]);

    const assignedDepartmentsCount = useMemo(() => {
        if (scopeType === 'Department') return selectedDepts.length;
        if (scopeType === 'All') return departmentsList.length;
        return 0;
    }, [scopeType, selectedDepts, departmentsList]);

    const assignedlocationsCount = useMemo(() => {
        if (scopeType === 'Location') return selectedLocations.length;
        if (scopeType === 'All') return locationsList.length;
        return 0;
    }, [scopeType, selectedLocations, locationsList]);

    useEffect(() => {
        if (selectedRecord) {
            if (Object.keys(selectedRecord).length > 0) {
                const tempObj: any = selectedRecord;
                setPolicyName(tempObj.AttendancePolicyName || '');
                setDescription(tempObj.Description || '');
                setLateArrivalMinutes(tempObj.LateArrivalMinutes ?? 0);
                setEarlyDepartureMinutes(tempObj.EarlyDepartureMinutes ?? 0);


                setPolicyStatusId(tempObj.AttendancePolicyStatusId || 2);

                if (tempObj.EffectiveFromDate) {
                    setEffectiveFromDate(new Date(tempObj.EffectiveFromDate));
                }
                if (tempObj.EffectiveToDate) {
                    setEffectiveToDate(new Date(tempObj.EffectiveToDate));
                }

                if (tempObj.CreatedDate) {
                    setCreatedDateStr(moment(tempObj.CreatedDate).format('MMM DD, YYYY - hh:mm A'));
                }
                if (tempObj.CreatedByEmployeeName) {
                    setCreatorName(tempObj.CreatedByEmployeeName);
                }
                if (tempObj.ModifiedDate) {
                    setModifiedDateStr(moment(tempObj.ModifiedDate).format('MMM DD, YYYY - hh:mm A'));
                }
                if (tempObj.ModifiedByEmployeeName) {
                    setModifiedByName(tempObj.ModifiedByEmployeeName);
                }

                if (tempObj.AccountEmployeeId && employeesDropdownList.length > 0) {
                    const matchedEmp = employeesDropdownList.find(
                        (emp: any) => emp.AccountEmployeeId === tempObj.AccountEmployeeId
                    );
                    if (matchedEmp) {
                        setSelectedPolicyEmployee({
                            value: matchedEmp.AccountEmployeeId,
                            label: matchedEmp.EmployeeName
                        });
                    }
                }

                if (Array.isArray(tempObj.StatusRules) && tempObj.StatusRules.length > 0) {
                    const mappedRules = tempObj.StatusRules.map((rule: any) => {
                        const matchedStatusObj = policyStatusTypeList.find(
                            (s) => s.SystemAttendancePolicyStatusTypeId === rule.SystemAttendancePolicyStatusTypeId
                        );
                        const matchedMethodObj = evaluationMethodsList.find(
                            (m) => m.SystemAttendancePolicyEvaluationMethodId === rule.SystemAttendancePolicyEvaluationMethodId
                        );
                        const currentStatusName = matchedStatusObj ? matchedStatusObj.StatusType : 'Present';
                        const currentMethodName = rule.IsDefault ? 'Default' : (matchedMethodObj ? matchedMethodObj.EvaluationMethod : 'Hours Worked');

                        let badgeColor = 'info';
                        if (currentStatusName === 'Present') badgeColor = 'success';
                        else if (currentStatusName === 'Short Day') badgeColor = 'warning';
                        else if (currentStatusName === 'Half Day') badgeColor = 'primary';
                        else if (currentStatusName === 'Absent') badgeColor = 'danger';

                        return {
                            id: rule.AccountAttendancePolicyStatusRuleId ? String(rule.AccountAttendancePolicyStatusRuleId) : `r${Math.random()}`,
                            order: rule.RuleSequence || 1,
                            status: currentStatusName,
                            evaluationMethod: currentMethodName,
                            operator: rule.IsDefault ? '-' : (rule.Operator || '>='),
                            value: rule.IsDefault ? '-' : String(rule.Value || 0),
                            color: badgeColor
                        };
                    });
                    setRules(mappedRules);
                } else {
                    setRules([]);
                }

                if (Array.isArray(tempObj.Scopes) && tempObj.Scopes.length > 0) {
                    const firstScope = tempObj.Scopes[0];

                    if (firstScope.ScopeTypeId === 2) {
                        setScopeType('Department');
                        const deptIds = tempObj.Scopes.map((s: any) => String(s.AccountDepartmentId));
                        setSelectedDepts(deptIds);
                        setSelectedLocations([]);
                        setSelectedEmployees([]);
                    } else if (firstScope.ScopeTypeId === 3) {
                        setScopeType('Location');
                        const locIds = tempObj.Scopes.map((s: any) => String(s.AccountLocationId));
                        setSelectedLocations(locIds);
                        setSelectedDepts([]);
                        setSelectedEmployees([]);
                    } else if (firstScope.ScopeTypeId === 4) {
                        setScopeType('Employee');
                        const empIds = tempObj.Scopes.map((s: any) => String(s.AccountEmployeeId));
                        setSelectedEmployees(empIds);
                        setSelectedDepts([]);
                        setSelectedLocations([]);
                    } else {
                        setScopeType('All');
                        setSelectedDepts([]);
                        setSelectedLocations([]);
                        setSelectedEmployees([]);
                    }
                } else {
                    setScopeType('All');
                }
            }
        }
    }, [selectedRecord, employeesDropdownList, policyStatusTypeList, evaluationMethodsList]);

    const getPolicyStatusType = async () => {
        try {
            const response = await fetchTimeliveAPI("api/AttendancePolicies/GetSystemAttendancePolicyStatusType", "GET", "", true);
            const json = await response.json();
            if (json && Array.isArray(json.Data)) {
                setPolicyStatusTypeList(json.Data);
            }
        } catch (error) {
            console.log("Error fetching evaluation methods:", error);
        }
    };

    const getEvaluationMethods = async () => {
        try {
            const response = await fetchTimeliveAPI("api/AttendancePolicies/GetSystemAttendancePolicyEvaluationMethod", "GET", "", true);
            const json = await response.json();
            if (json && Array.isArray(json.Data)) {
                setEvaluationMethodsList(json.Data);
            }
        } catch (error) {
            console.log("Error fetching evaluation methods:", error);
        }
    };

    const getdepartments = async () => {
        try {
            const response = await fetchTimeliveAPI("api/Departments", "GET", "", true);
            const json = await response.json();
            if (json && Array.isArray(json.Data)) {
                setDepartmentsList(json.Data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getLocations = async () => {
        try {
            const response = await fetchTimeliveAPI("api/AttendancePolicies/GetAccountLocations", "GET", "", true);
            const json = await response.json();
            if (json && Array.isArray(json.Data)) {
                setLocationsList(json.Data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getEmployeesData = async () => {
        try {
            const response = await fetchTimeliveAPI("api/AttendancePolicies/GetAccountEmployees", "GET", "", true);
            const json = await response.json();
            if (json && Array.isArray(json.Data)) {
                setEmployeesList(json.Data);
                setEmployeesDropdownList(json.Data);
                const currentEmp = json.Data.find((emp: any) => emp.AccountEmployeeId === sessionAccountEmployeeId);
                if (currentEmp) {
                    setSelectedPolicyEmployee({
                        value: currentEmp.AccountEmployeeId,
                        label: currentEmp.EmployeeName
                    });
                    setCreatorName(currentEmp.EmployeeName || '');
                    setCreatedDateStr(moment().format('MMM DD, YYYY - hh:mm A'));
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getdepartments();
        getLocations();
        getEmployeesData();
        getEvaluationMethods();
        getPolicyStatusType();
    }, []);
    const checkIsDuplicateName = (nameToTest: string) => {
        if (!nameToTest || !nameToTest.trim()) return false;

        return policyList.some((p: any) => {
            const isSameName = p.AttendancePolicyName?.trim().toLowerCase() === nameToTest.trim().toLowerCase();
            const isDifferentPolicy = Number(p.AccountAttendancePolicyId) !== Number(policyId);
            return isSameName && isDifferentPolicy;
        });
    };

    const validateField = (name: string, value: any) => {
        let errorMsg = "";
        if (name === "policyName") {
            if (!value || !value.trim()) {
                errorMsg = "Policy Name is required.";
            } else if (checkIsDuplicateName(value)) {
                errorMsg = "A policy with this name already exists.";
            }
        }
       
        if (name === "creator") {
            if (!value || !value.value) errorMsg = "Policy Creator is required.";
        }
        if (name === "lateArrival") {
            if (value === undefined || value === null || value === '' || isNaN(Number(value))) {
                errorMsg = "Late Arrival Minutes are required.";
            } else if (Number(value) < 0) {
                errorMsg = "Minutes cannot be negative.";
            }
        }
        if (name === "earlyDeparture") {
            if (value === undefined || value === null || value === '' || isNaN(Number(value))) {
                errorMsg = "Early Departure Minutes are required.";
            } else if (Number(value) < 0) {
                errorMsg = "Minutes cannot be negative.";
            }
        }

        if (name === "scope") {
            if (scopeType === 'Department' && selectedDepts.length === 0) {
                errorMsg = "Policy has no assignee";
            } else if (scopeType === 'Location' && selectedLocations.length === 0) {
                errorMsg = "Policy has no assignee";
            } else if (scopeType === 'Employee' && selectedEmployees.length === 0) {
                errorMsg = "Policy has no assignee";
            }
        }

        setErrors(prev => ({ ...prev, [name]: errorMsg }));
        return errorMsg;
    };

    const toggleDept = (id: any) => {
        const stringId = String(id);
        setSelectedDepts(prev => prev.includes(stringId) ? prev.filter(d => d !== stringId) : [...prev, stringId]);
    };

    const toggleLocation = (id: any) => {
        const stringId = String(id);
        setSelectedLocations(prev => prev.includes(stringId) ? prev.filter(l => l !== stringId) : [...prev, stringId]);
    };

    const toggleEmployee = (id: any) => {
        const stringId = String(id);
        setSelectedEmployees(prev => prev.includes(stringId) ? prev.filter(e => e !== stringId) : [...prev, stringId]);
    };

    const filteredDepartments = useMemo(() => {
        return departmentsList.filter(dept => {
            const deptName = dept.DepartmentName || '';
            return deptName.toLowerCase().includes(deptSearch.toLowerCase());
        });
    }, [departmentsList, deptSearch]);

    const filteredLocations = useMemo(() => {
        if (!locationsList || !Array.isArray(locationsList)) return [];
        return locationsList.filter(loc => {
            const locName = loc.AccountLocation || '';
            return locName.toLowerCase().includes(locationSearch.toLowerCase());
        });
    }, [locationsList, locationSearch]);

    const paginatedLocations = useMemo(() => {
        if (!filteredLocations || !Array.isArray(filteredLocations)) return [];
        return filteredLocations.slice(0, locationLoadCount);
    }, [filteredLocations, locationLoadCount]);

    const filteredEmployees = useMemo(() => {
        if (!employeesList || !Array.isArray(employeesList)) return [];
        return employeesList.filter((emp) => {
            const name = emp.EmployeeName ? String(emp.EmployeeName).toLowerCase() : '';
            const email = emp.EMailAddress ? String(emp.EMailAddress).toLowerCase() : '';
            const search = employeeSearch ? employeeSearch.toLowerCase() : '';
            if (!search) return true;
            return name.includes(search) || email.includes(search);
        });
    }, [employeesList, employeeSearch]);

    const paginatedEmployees = useMemo(() => {
        if (!filteredEmployees || !Array.isArray(filteredEmployees)) return [];
        return filteredEmployees.slice(0, employeeLoadCount);
    }, [filteredEmployees, employeeLoadCount]);

    function updateRule(id: string, patch: Partial<StatusRule>) {
        setRules((prev) => prev.map((r) => {
            if (r.id === id) {
                const updated = { ...r, ...patch };
                if (patch.status && TONE_COLORS[patch.status]) updated.color = TONE_COLORS[patch.status];
                return updated;
            }
            return r;
        }));
    }

    function deleteRule(id: string) {
        setRules((prev) => prev.filter((r) => r.id !== id));
    }

    function addRule() {
        const id = `r${Date.now()}`;
        
        const availableStatus = policyStatusTypeList.find(
            (statusObj) => !rules.some((r) => r.status === statusObj.StatusType)
        );

        if (!availableStatus) {
            alert("All attendance statuses have already been configured.");
            return;
        }

        const defaultStatus = availableStatus.StatusType;
        const defaultMethod = evaluationMethodsList[0]?.EvaluationMethod || 'Hours Worked';

        setRules((prev) => [
            ...prev,
            {
                id,
                order: prev.length + 1,
                status: defaultStatus,
                evaluationMethod: defaultMethod,
                operator: '>=',
                value: '0',
                color: 'success'
            },
        ]);
        setEditingId(id);
    }

    function handleDrop(targetIndex: number) {
        const from = dragIndex.current;
        if (from === null || from === targetIndex) return;
        setRules((prev) => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(targetIndex, 0, moved);
            return next.map((rule, idx) => ({ ...rule, order: idx + 1 }));
        });
        dragIndex.current = null;
        setDragOver(null);
    }

    const handleSavePolicy = async (statusId: number) => {
        const e1 = validateField("policyName", policyName);
        const e2 = validateField("creator", selectedPolicyEmployee);
        const e3 = validateField("lateArrival", lateArrivalMinutes);
        const e4 = validateField("earlyDeparture", earlyDepartureMinutes);
        const e5 = validateField("scope", scopeType);

        if (checkIsDuplicateName(policyName)) {
            ShowToast("Policy name already exists.", "Warning", "warning", "toast-top-right");
            return; 
        }

        if (e1 || e2 || e3 || e4 || e5) {
            if (e5) ShowToast("Policy has no assignee", "Warning", "warning", "toast-top-right");
            return;
        }

        const formattedRules = rules.map((rule, index) => {
            // const numericValue = Number(String(rule.value).replace(/[^0-9]/g, '')) || 0;
            const numericValue = isNaN(parseFloat(rule.value)) ? 0 : parseFloat(rule.value);
            const matchedStatus = policyStatusTypeList.find((s) => s.StatusType === rule.status);
            const statusTypeId = matchedStatus ? matchedStatus.SystemAttendancePolicyStatusTypeId : 0;
            const matchedMethod = evaluationMethodsList.find((m) => m.EvaluationMethod === rule.evaluationMethod);
            const evalMethodId = matchedMethod ? matchedMethod.SystemAttendancePolicyEvaluationMethodId : 0;

            return {
                AccountAttendancePolicyStatusRuleId: 0,
                RuleSequence: index + 1,
                SystemAttendancePolicyStatusTypeId: statusTypeId,
                SystemAttendancePolicyEvaluationMethodId: evalMethodId,
                Operator: rule.operator === '-' ? '' : rule.operator,
                Value: numericValue,
                IsDefault: rule.evaluationMethod === 'Default'
            };
        });

        setPolicyStatusId(statusId);

        let formattedScopes: any[] = [];
        const scopeTypeMapping: Record<string, number> = { 'All': 1, 'Department': 2, 'Location': 3, 'Employee': 4 };
        const currentScopeId = scopeTypeMapping[scopeType] || 1;

        if (scopeType === 'Department') {
            formattedScopes = selectedDepts.map(deptId => ({
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: Number(deptId),
                AccountLocationId: 0,
                AccountEmployeeId: 0
            }));
        } else if (scopeType === 'Location') {
            formattedScopes = selectedLocations.map(locId => ({
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: 0,
                AccountLocationId: Number(locId) || 0,
                AccountEmployeeId: 0
            }));
        } else if (scopeType === 'Employee') {
            formattedScopes = selectedEmployees.map(empId => ({
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: 0,
                AccountLocationId: 0,
                AccountEmployeeId: Number(empId)
            }));
        } else {
            formattedScopes = [{
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: 0,
                AccountLocationId: 0,
                AccountEmployeeId: 0
            }];
        }

        const payload: any = {
            AccountAttendancePolicyId: isEditMode ? Number(policyId) : 0,
            AccountId: sessionAccountId,
            AccountEmployeeId: selectedPolicyEmployee ? selectedPolicyEmployee.value : sessionAccountEmployeeId,
            AttendancePolicyName: policyName,
            Description: description,
            AttendancePolicyStatusId: statusId,
            EffectiveFromDate: effectiveFromDate ? moment(effectiveFromDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : new Date().toISOString(),
            EffectiveToDate: effectiveToDate ? moment(effectiveToDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
            LateArrivalMinutes: lateArrivalMinutes !== undefined ? Number(lateArrivalMinutes) : 0,
            EarlyDepartureMinutes: earlyDepartureMinutes !== undefined ? Number(earlyDepartureMinutes) : 0,
            StatusRules: formattedRules,
            Scopes: formattedScopes,
            IsApplyChanges: false
        };

        try {
            if (isEditMode) {
                const resultAction = await dispatch(updateAttendancePolicyAsync(payload));
                if (updateAttendancePolicyAsync.fulfilled.match(resultAction)) {
                    const responseData = resultAction.payload;
                    if (responseData && (responseData.StatusCode === "200" || responseData.Data !== null)) {
                        const msg = statusId === 2 ? "Policy Saved as Draft" : "Policy Updated";
                        ShowToast(msg, 'Success', "success", "toast-top-right");
                        setIsAssignmentEditing(false);
                        localStorage.removeItem('objAttendancePolicyItem');
                    } else {
                        ShowToast(responseData?.ResponseMessage || 'Failed to update policy', "Error", "error", "toast-top-right");
                    }
                } else {
                    ShowToast('Failed to update policy via Redux', "Error", "error", "toast-top-right");
                }
            } else {
                const response = await fetchTimeliveAPI(
                    "api/AttendancePolicies/AddAccountAttendancePolicy", "POST", payload, true
                );
                const responseData = await response.json();
                setPolicyId(responseData.Id);

                if (response.ok) {
                    ShowToast("Policy Saved as Draft Successfully", 'Success', "success", "toast-top-right");
                } else {
                    console.error("Server Error Status:", response.status);
                    ShowToast('Failed to add policy', "Error", "error", "toast-top-right");
                }
            }
        } catch (error) {
            console.error("API Operations Error:", error);
            ShowToast('Network Error', "Error", "error", "toast-top-right");
        }
    };

    const handleActivatePolicy = async () => {
        const payload = {
            AccountAttendancePolicyId: policyId,
            AttendancePolicyStatusId: 1 
        };

        try {
            const resultAction = await dispatch(activateAttendancePolicyAsync(payload));
            
            if (activateAttendancePolicyAsync.fulfilled.match(resultAction)) {
                const responseData = resultAction.payload;           
              
                if (responseData && responseData.StatusCode === "200") {
                    ShowToast("Policy Activated Successfully", 'Success', "success", "toast-top-right");
                    setPolicyStatusId(1);
                    localStorage.removeItem('objAttendancePolicyItem');
                    navigate('/attendancePolicyList');
                } else {

                    let rawData = responseData?.Data;

                    if (Array.isArray(rawData) && rawData.length > 0) {
                        rawData = rawData[0];
                    } else if (!rawData) {
                        rawData = responseData?.ResponseMessage;
                    }

                    let parsedData = null;

                    try {
                        parsedData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
                    } catch (e) {
                        parsedData = null;
                    }

                    if (parsedData && parsedData.Conflicts && Array.isArray(parsedData.Conflicts)) {
                        setConflictData(parsedData);
                        setModalOpen(true);
                    } else {
                        const errorMessage = typeof rawData === 'string' ? rawData : 'Failed to activate policy';  
                        if (errorMessage.toLowerCase().includes("overlap")) {
                            ShowToast(errorMessage, "Warning", "warning", "toast-top-right");
                        } else {
                            ShowToast(errorMessage, "Error", "error", "toast-top-right");
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Activation Error:", error);
            ShowToast('Network Error', "Error", "error", "toast-top-right");
        }
    };
    const handleArchivePolicy = async () => {
        const payload: any = {
            AccountAttendancePolicyId: Number(policyId),
            AttendancePolicyStatusId: 3
        };

        try {
            const resultAction = await dispatch(archiveAttendancePolicyAsync(payload));
            if (archiveAttendancePolicyAsync.fulfilled.match(resultAction)) {
                const responseData = resultAction.payload;
                if (responseData && (responseData.StatusCode === "200" || responseData.Data !== null)) {
                    ShowToast("Policy Archived Successfully", 'Success', "success", "toast-top-right");
                    setPolicyStatusId(3);
                    setIsAssignmentEditing(false);
                } else {
                    ShowToast(responseData?.ResponseMessage || 'Failed to archive policy', "Error", "error", "toast-top-right");
                }
            }
        } catch (error) {
            console.error("Archive Error:", error);
            ShowToast('Network Error', "Error", "error", "toast-top-right");
        }
    };

    const handleUpdateOnlyAssignments = async () => {
        const scopeError = validateField("scope", scopeType);
        if (scopeError) {
            ShowToast("Policy has no assignee", "Warning", "warning", "toast-top-right");
            return;
        }
        let formattedScopes: any[] = [];
        const scopeTypeMapping: Record<string, number> = { 'All': 1, 'Department': 2, 'Location': 3, 'Employee': 4 };
        const currentScopeId = scopeTypeMapping[scopeType] || 1;

        if (scopeType === 'Department') {
            formattedScopes = selectedDepts.map(deptId => ({
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: Number(deptId),
                AccountLocationId: 0,
                AccountEmployeeId: 0
            }));
        } else if (scopeType === 'Location') {
            formattedScopes = selectedLocations.map(locId => ({
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: 0,
                AccountLocationId: Number(locId) || 0,
                AccountEmployeeId: 0
            }));
        } else if (scopeType === 'Employee') {
            formattedScopes = selectedEmployees.map(empId => ({
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: 0,
                AccountLocationId: 0,
                AccountEmployeeId: Number(empId)
            }));
        } else {
            formattedScopes = [{
                AccountAttendancePolicyScopeId: 0,
                ScopeTypeId: currentScopeId,
                AccountDepartmentId: 0,
                AccountLocationId: 0,
                AccountEmployeeId: 0
            }];
        }

        const payload: any = {
            AccountAttendancePolicyId: Number(policyId),
            AccountId: sessionAccountId,
            AccountEmployeeId: selectedPolicyEmployee ? selectedPolicyEmployee.value : sessionAccountEmployeeId,
            AttendancePolicyName: policyName,
            Description: description,
            AttendancePolicyStatusId: policyStatusId, 
            EffectiveFromDate: effectiveFromDate ? moment(effectiveFromDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : new Date().toISOString(),
            EffectiveToDate: effectiveToDate ? moment(effectiveToDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
            LateArrivalMinutes: lateArrivalMinutes !== undefined ? Number(lateArrivalMinutes) : 0,
            EarlyDepartureMinutes: earlyDepartureMinutes !== undefined ? Number(earlyDepartureMinutes) : 0,
            StatusRules: rules.map((rule, index) => {
                // const numericValue = Number(String(rule.value).replace(/[^0-9]/g, '')) || 0;
               const numericValue = isNaN(parseFloat(rule.value)) ? 0 : parseFloat(rule.value);
                const matchedStatus = policyStatusTypeList.find((s) => s.StatusType === rule.status);
                return {
                    AccountAttendancePolicyStatusRuleId: 0,
                    RuleSequence: index + 1,
                    SystemAttendancePolicyStatusTypeId: matchedStatus ? matchedStatus.SystemAttendancePolicyStatusTypeId : 0,
                    SystemAttendancePolicyEvaluationMethodId: evaluationMethodsList.find((m) => m.EvaluationMethod === rule.evaluationMethod)?.SystemAttendancePolicyEvaluationMethodId || 0,
                    Operator: rule.operator === '-' ? '' : rule.operator,
                    Value: numericValue,
                    IsDefault: rule.evaluationMethod === 'Default'
                };
            }),
            Scopes: formattedScopes,
            IsApplyChanges: true
        };

        try {
            const resultAction = await dispatch(updateAttendancePolicyAsync(payload));
            if (updateAttendancePolicyAsync.fulfilled.match(resultAction)) {
                const responseData = resultAction.payload;
                if (responseData && (responseData.StatusCode === "200" || responseData.Data !== null)) {
                    ShowToast("Assignments Updated Successfully in DB", 'Success', "success", "toast-top-right");
                    setIsAssignmentEditing(false);
                } else {
                    ShowToast(responseData?.ResponseMessage || 'Failed to Scope Changes', "Error", "error", "toast-top-right");
                }
            }
        } catch (error) {
             console.error("Assignment Update Error:", error);
             ShowToast('Network Error', "Error", "error", "toast-top-right");
        }
    };

    const statusBadge = getStatusLabelAndClass(policyStatusId);

    return (
        <div className="p-4 min-vh-100">
            <div className="d-flex align-items-end justify-content-between mb-6">
                <div>
                    <div className="d-flex align-items-center gap-2">
                        <h3 className="fs-4 fw-bold mb-0 text-dark">
                            {isEditMode ? getValueFromResource('Update Attendance Policy', dispatch, t) : getValueFromResource('Add Attendance Policy', dispatch, t)}
                        </h3>
                        <span className={`badge ${statusBadge.className} border rounded-pill px-2.5 py-0.5 fs-6 fw-semibold`}>
                            {statusBadge.label}
                        </span>
                    </div>
                    <p className="text-muted fs-7 mb-0 mt-1">{getValueFromResource('Configure how attendance status is calculated for assigned employees.', dispatch, t)}</p>
                </div>                

                <div className="d-flex gap-3" style={{ marginBottom: '2px' }}>
                    <button type="button" className="btn btn-sm btn-secondary fs-7 px-5 fw-medium" onClick={() => navigate('/attendancePolicyList')}>Cancel</button>
                    
                    {policyStatusId === 2 && (
                        <>
                            <button type="button" className="btn btn-sm btn-light border fs-7 px-5 fw-medium text-gray-700" onClick={() => handleSavePolicy(2)}>Save Draft</button>
                            <button 
                                type="button" className="btn btn-sm btn-primary fs-7 px-5 fw-bold" 
                                onClick={handleActivatePolicy}  
                                disabled={!policyId}> 
                                Activate Policy
                            </button>
                        </>
                    )}

                    {isPolicyActive && (
                        <button 
                            type="button" className="btn btn-sm btn-primary fs-7 px-5 fw-bold" onClick={handleArchivePolicy}>
                            Archive Policy
                        </button> 
                    )}

                    {isPolicyArchived && (
                        <button 
                            type="button" className="btn btn-sm btn-primary fs-7 px-5 fw-bold" onClick={() => handleSavePolicy(2)}>
                            <i className="bi bi-arrow-counterclockwise me-1"></i> Revert to Draft
                        </button> 
                    )}
                </div>               
            </div>
            
            <div className="row">
                <div className="col-9 d-flex flex-column gap-7">

                    <div className="policycard" style={{ pointerEvents: isDisabled ? 'none' : 'auto', opacity: isDisabled ? 0.75 : 1 }}>
                        <div className="card-header bg-white border-bottom mb-2">
                            <div className="card-title d-flex flex-column">
                                <h4 className="fw-bold fs-6 text-dark">{getValueFromResource('Policy Details', dispatch, t)}</h4>
                                <span className="text-muted fs-8 mt-1 mb-2">General information that identifies this attendance policy.</span>
                            </div>
                        </div>

                        <div>
                            <div className="row mb-3">
                                <div className="col-12 col-md-6 mb-2">
                                    <label className="fs-7 fw-semibold text-gray-700 mb-2">{getValueFromResource('Policy Name', dispatch, t)} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        disabled={isDisabled}
                                        className={`form-control fs-7 h-40px px-3 input-bg ${errors.policyName ? 'border-danger' : ''}`}
                                        value={policyName}
                                        onChange={(e) => {
                                            setPolicyName(e.target.value);
                                            validateField("policyName", e.target.value);
                                        }}
                                        onBlur={(e) => validateField("policyName", e.target.value)}
                                    />
                                    {errors.policyName && <div className="text-danger fs-8 mt-1 fw-medium">{errors.policyName}</div>}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="fs-7 fw-semibold text-gray-700 m-0">{getValueFromResource('Description', dispatch, t)}</label>
                                        <span className="text-muted fs-8">Optional</span>
                                    </div>
                                    <textarea disabled={isDisabled} className="form-control fs-7 px-3 py-2 input-bg" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-12 col-md-6 mb-2">
                                    <label className="fs-7 fw-semibold text-gray-700 mb-2">{getValueFromResource('Effective From', dispatch, t)}</label>
                                    <div className="wrapper">
                                        <DatePicker disabled={isDisabled} selected={effectiveFromDate} onChange={(date: Date | null) => setEffectiveFromDate(date)} calendarStartDay={calendarStartDay} className="form-control dataPicker form-control-dark btn-icon-dark btn-text-dark fs-7 fw-normal input-bg" dateFormat={CurrentCultureDateFormat.toString()} placeholderText="Select Effective From" />
                                        <i className="insideField-Icons ki-outline ki-calendar text-gray"></i>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 mb-2">
                                    <label className="fs-7 fw-semibold text-gray-700 mb-2">{getValueFromResource('Effective To', dispatch, t)}</label>
                                    <div className="wrapper">
                                        <DatePicker disabled={isDisabled} selected={effectiveToDate} onChange={(date: Date | null) => setEffectiveToDate(date)} calendarStartDay={calendarStartDay} className="form-control dataPicker form-control-dark btn-icon-dark btn-text-dark fs-7 fw-normal input-bg" dateFormat={CurrentCultureDateFormat.toString()} placeholderText="Select Effective To" minDate={effectiveFromDate} />
                                        <i className="insideField-Icons ki-outline ki-calendar text-gray"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="policycard">
                        <div>
                            <div className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2">
                                <h4 className="fw-bold fs-6 text-dark d-flex align-items-center mb-0">
                                    <i className="ki-duotone ki-abstract-26 bg-light-primary text-primary fs-4 me-2"><span className="path1"></span><span className="path2"></span></i>
                                    {getValueFromResource('Policy Scope', dispatch, t)}
                                </h4>
                                
                                {isPolicyActive && (
                                    <div className="d-flex gap-2">
                                        {!isAssignmentEditing ? (
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-light-primary fw-bold fs-7"
                                                onClick={() => setIsAssignmentEditing(true)}>
                                                <i className="bi bi-pencil me-1"></i> Edit Assignments
                                            </button>
                                        ) : (
                                            <>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-sm btn-secondary fs-7 px-5 fw-medium"
                                                    onClick={() => setIsAssignmentEditing(false)}>
                                                    Cancel
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-sm btn-success fw-bold fs-7"
                                                    onClick={handleUpdateOnlyAssignments}>
                                                    Apply Changes
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={{ pointerEvents: (!isDisabled || isAssignmentEditing) ? 'auto' : 'none', opacity: (!isDisabled || isAssignmentEditing) ? 1 : 0.85 }}>
                                <label className="fs-7 fw-semibold text-gray-700 mb-3 ">{getValueFromResource('Scope Type', dispatch, t)}</label>
                                <div className="row g-3 mb-4">
                                    {['All', 'Department', 'Location', 'Employee'].map((type) => (
                                        <div className="col-6 col-md-3" key={type}>
                                            <button type="button" className={`scope-btn w-100 ${scopeType === type ? 'active' : ''}`} onClick={() => setScopeType(type as any)}>
                                                {type === 'All' ? 'All Organization' : `By ${type}`}
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {scopeType === 'Department' && (
                                    <div className="mb-4 mt-3">
                                        <label className="fs-7 fw-semibold text-gray-800 mb-2">Select Departments</label>
                                        <div className="position-relative mb-3">
                                            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-gray-400 fs-7"></i>
                                            <input type="text" className="form-control form-control-solid form-control-sm fs-7 ps-9" placeholder="Search departments..." value={deptSearch} onChange={(e) => setDeptSearch(e.target.value)} />
                                        </div>
                                        <div className="border rounded p-2 overflow-auto" style={{ maxHeight: '200px', backgroundColor: '#fcfcfc' }}>
                                            {filteredDepartments.map((dept) => (
                                                <label key={String(dept.AccountDepartmentId)} className="d-flex align-items-center gap-3 px-3 py-2 rounded cursor-pointer bg-hover-light user-select-none transition-all">
                                                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                                        <input type="checkbox" className="form-check-input widget-8-check" checked={selectedDepts.includes(String(dept.AccountDepartmentId))} onChange={() => toggleDept(dept.AccountDepartmentId)} />
                                                    </div>
                                                    <span className="fs-7 text-gray-800 fw-medium">{dept.DepartmentName}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {scopeType === 'Location' && (
                                    <div className="mb-4 mt-3">
                                        <label className="fs-7 fw-semibold text-gray-800 mb-2">Select Locations</label>
                                        <div className="position-relative mb-3">
                                            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-gray-400 fs-7"></i>
                                            <input type="text" className="form-control form-control-solid form-control-sm fs-7 ps-9" placeholder="Search locations..." value={locationSearch} onChange={(e) => { setLocationSearch(e.target.value); setLocationLoadCount(10); }} />
                                        </div>
                                        <div className="border rounded p-2 overflow-auto" style={{ maxHeight: '200px', backgroundColor: '#fcfcfc' }}>
                                            {paginatedLocations.length === 0 ? (
                                                <p className="text-muted fs-7 italic text-center py-3">No locations found</p>
                                            ) : (
                                                paginatedLocations.map((loc: any) => {
                                                    if (!loc) return null;
                                                    const locIdStr = String(loc.AccountLocationId);
                                                    return (
                                                        <label key={locIdStr} className="d-flex align-items-center gap-3 px-3 py-2 rounded cursor-pointer bg-hover-light user-select-none transition-all">
                                                            <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                <input type="checkbox" className="form-check-input widget-8-check" checked={selectedLocations.includes(locIdStr)} onChange={() => toggleLocation(locIdStr)} />
                                                            </div>
                                                            <span className="fs-7 text-gray-800 fw-medium">{loc.AccountLocation || 'Unnamed Location'}</span>
                                                        </label>
                                                    );
                                                })
                                            )}

                                            {filteredLocations.length > locationLoadCount && (
                                                <div 
                                                    onClick={() => {
                                                        if (isLoadingMoreLocation) return;
                                                        setIsLoadingMoreLocation(true);
                                                        setTimeout(() => {
                                                            setLocationLoadCount(prev => prev + 10);
                                                            setIsLoadingMoreLocation(false);
                                                        }, 600);
                                                    }} 
                                                    className={`d-flex align-items-center justify-content-center px-3 py-1 mt-1 rounded text-center user-select-none transition-all ${isLoadingMoreLocation ? 'bg-light text-muted' : 'cursor-pointer bg-light-primary text-primary bg-hover-primary-subtle'}`}
                                                    style={{ fontWeight: 600, minHeight: '28px' }} 
                                                >
                                                    {isLoadingMoreLocation ? (
                                                        <div className="d-flex align-items-center gap-2 fs-8">
                                                            <span className="spinner-border text-primary" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                                                            <span>Loading...</span>
                                                        </div>
                                                    ) : (
                                                        <span className="fs-8 fw-bold d-inline-flex align-items-center"> 
                                                            <i className="bi bi-plus fs-6 me-1"></i> Load More Locations...
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {scopeType === 'Employee' && (
                                    <div className="mb-4 mt-3">
                                        <label className="fs-7 fw-semibold text-gray-800 mb-2">Select Employees</label>
                                        <div className="position-relative mb-3">
                                            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-gray-400 fs-7"></i>
                                            <input type="text" className="form-control form-control-solid form-control-sm fs-7 ps-9" placeholder="Search by name or email..." value={employeeSearch} onChange={(e) => { setEmployeeSearch(e.target.value); setEmployeeLoadCount(10); }} />
                                        </div>
                                        
                                        <div className="border rounded p-2 overflow-auto" style={{ maxHeight: '200px', backgroundColor: '#fcfcfc' }}>
                                            {paginatedEmployees.length === 0 ? (
                                                <p className="text-muted fs-7 italic text-center py-3">No employees found</p>
                                            ) : (
                                                paginatedEmployees.map((emp: any) => {
                                                    if (!emp) return null;
                                                    const empIdStr = String(emp.AccountEmployeeId);
                                                    return (
                                                        <label key={empIdStr} className="d-flex align-items-center gap-3 px-3 py-2 rounded cursor-pointer bg-hover-light user-select-none transition-all">
                                                            <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                <input type="checkbox" className="form-check-input widget-8-check" checked={selectedEmployees.includes(empIdStr)} onChange={() => toggleEmployee(empIdStr)} />
                                                            </div>
                                                            <div className="d-flex flex-column lh-sm">
                                                                <span className="fs-7 fw-semibold text-gray-800">{emp.EmployeeName || 'Unnamed'}</span>
                                                                {emp.EMailAddress && <span className="text-muted fs-8">{emp.EMailAddress}</span>}
                                                            </div>
                                                        </label>
                                                    );
                                                })
                                            )}

                                            {filteredEmployees.length > employeeLoadCount && (
                                                <div 
                                                    onClick={() => {
                                                        if (isLoadingMore) return;
                                                        setIsLoadingMore(true);
                                                        setTimeout(() => {
                                                            setEmployeeLoadCount(prev => prev + 10);
                                                            setIsLoadingMore(false);
                                                        }, 600);
                                                    }} 
                                                    className={`d-flex align-items-center justify-content-center px-3 py-1 mt-1 rounded text-center user-select-none transition-all ${isLoadingMore ? 'bg-light text-muted' : 'cursor-pointer bg-light-primary text-primary bg-hover-primary-subtle'}`}
                                                    style={{ fontWeight: 600, minHeight: '28px' }} 
                                                >
                                                    {isLoadingMore ? (
                                                        <div className="d-flex align-items-center gap-2 fs-8">
                                                            <span className="spinner-border text-primary" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                                                            <span>Loading...</span>
                                                        </div>
                                                    ) : (
                                                        <span className="fs-8 fw-bold d-inline-flex align-items-center"> 
                                                            <i className="bi bi-plus fs-6 me-1"></i> Load More Employees...
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {scopeType !== 'All' && (
                                    <div className="mt-3 pt-3 border-top">
                                        <label className="fs-7 fw-semibold text-gray-700 mb-2 d-block">Selected Filters:</label>
                                        <div className="d-flex flex-wrap gap-2">

                                            {scopeType === 'Department' && departmentsList.filter(d => selectedDepts.includes(String(d.AccountDepartmentId))).map(dept => (
                                                <span key={String(dept.AccountDepartmentId)} className="badge bg-light-primary text-primary border border-primary-subtle px-3 py-1.5 fs-7 rounded-pill d-inline-flex align-items-center gap-2">
                                                    {dept.DepartmentName}
                                                    <i className="bi bi-x fs-5 cursor-pointer text-primary" onClick={() => toggleDept(dept.AccountDepartmentId)}></i>
                                                </span>
                                            ))}

                                            {scopeType === 'Location' && locationsList.filter(l => selectedLocations.includes(String(l.AccountLocationId))).map(loc => (
                                                <span key={String(loc.AccountLocationId)} className="badge bg-light-primary text-primary border border-primary-subtle px-3 py-1.5 fs-7 rounded-pill d-inline-flex align-items-center gap-2">
                                                    {loc.AccountLocation}
                                                    <i className="bi bi-x fs-5 cursor-pointer text-primary" onClick={() => toggleLocation(loc.AccountLocationId)}></i>
                                                </span>
                                            ))}

                                            {scopeType === 'Employee' && selectedEmployees.map(empId =>
                                                employeesList.find(e => String(e?.AccountEmployeeId) === String(empId))
                                            ).filter(Boolean).map((emp: any) => (
                                                <span key={String(emp.AccountEmployeeId)} className="badge bg-light-primary text-primary border border-primary-subtle px-3 py-1.5 fs-7 rounded-pill d-inline-flex align-items-center gap-2">
                                                    {emp.EmployeeName}
                                                    <i className="bi bi-x fs-5 cursor-pointer text-primary" onClick={() => toggleEmployee(emp.AccountEmployeeId)}></i>
                                                </span>
                                            ))}

                                            {((scopeType === 'Department' && selectedDepts.length === 0) ||
                                                (scopeType === 'Location' && selectedLocations.length === 0) ||
                                                (scopeType === 'Employee' && selectedEmployees.length === 0)) && (
                                                    <span className="text-muted fs-7 italic">None selected</span>
                                                )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="policycard" style={{ pointerEvents: isDisabled ? 'none' : 'auto', opacity: isDisabled ? 0.75 : 1 }}>
                        <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                            <div className="card-title d-flex flex-column mb-3">
                                <h4 className="fw-bold fs-6 text-dark m-0">{getValueFromResource('Attendance Status Configuration', dispatch, t)}</h4>
                                <span className="text-muted fs-8 mt-1 mb-2">{getValueFromResource(' Rules are evaluated from top to bottom. The first matching condition determines the employee`s attendance status for the day.', dispatch, t)}</span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {/* Grey Guideline Button */}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary text-gray-700 d-flex align-items-center gap-1 fs-7 px-3"
                                    onClick={() => setGuidelineModalOpen(true)}
                                >
                                    <i className="bi bi-info-circle fs-6"></i> Guideline
                                </button>

                                {!isDisabled && (
                                    <button type="button"
                                        onClick={addRule}
                                        className="btn btn-sm btn-primary d-flex align-items-center gap-1 fs-7 px-4">
                                        <i className="bi bi-plus fs-5"></i> {getValueFromResource('Add Status', dispatch, t)}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="table">
                            <table className="table table-row-gray-300 mb-0 text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
                                        <th className="w-10 px-4 py-3" />
                                        <th className="w-14 px-4 py-3 text-left font-semibold text-gray-500 fs-7">Order</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-500 fs-7">Attendance Status</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-500 fs-7">Evaluation Method</th>
                                        <th className="w-24 px-4 py-3 text-left font-semibold text-gray-500 fs-7">Operator</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-500 fs-7">Value</th>
                                        <th className="w-16 px-4 py-3 text-right font-semibold text-gray-500 fs-7">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rules.map((rule, index) => {
                                        const editing = editingId === rule.id;
                                        const isDefault = rule.evaluationMethod === "Default";
                                        const currentStatusName = rule.status || 'Present';

                                        let currentColor = 'info';
                                        switch (currentStatusName) {
                                            case 'Present': currentColor = 'success'; break;
                                            case 'Short Day': currentColor = 'warning'; break;
                                            case 'Half Day': currentColor = 'primary'; break;
                                            case 'Absent': currentColor = 'danger'; break;
                                            default: currentColor = 'info'; break;
                                        }

                                        return (
                                            <tr
                                                key={rule.id}
                                                draggable={!isDisabled}
                                                onDragStart={() => (dragIndex.current = index)}
                                                onDragOver={(e) => { e.preventDefault(); setDragOver(index); }}
                                                onDrop={() => handleDrop(index)}
                                                onDragEnd={() => setDragOver(null)}
                                                className="align-middle border-bottom border-gray-100"
                                                style={{ backgroundColor: dragOver === index ? '#f4f6ea' : 'transparent' }}
                                            >
                                                <td className="text-center py-3.5 align-middle">
                                                    <div className="d-inline-flex align-items-center text-gray-400" style={{ cursor: isDisabled ? 'not-allowed' : 'grab', gap: '2px' }}>
                                                        <div className="d-flex flex-column gap-1">
                                                            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#b5b5c3' }} />
                                                            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#b5b5c3' }} />
                                                            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#b5b5c3' }} />
                                                        </div>
                                                        <div className="d-flex flex-column gap-1">
                                                            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#b5b5c3' }} />
                                                            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#b5b5c3' }} />
                                                            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#b5b5c3' }} />
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="text-start py-3.5 align-middle">
                                                    <span className="d-inline-flex align-items-center justify-content-center text-gray-600 fw-bold fs-7" style={{ width: '26px', height: '26px', backgroundColor: '#f4f6f9', borderRadius: '50%' }}>
                                                        {index + 1}
                                                    </span>
                                                </td>

                                                <td className="text-start py-3.5 align-middle">
                                                    {editing && !isDisabled ? (
                                                        <select
                                                            value={rule.status}
                                                            onChange={(e) => updateRule(rule.id, { status: e.target.value })}
                                                            className="form-select form-select-sm fs-7 py-1 px-2 w-160px"
                                                        >
                                                            {policyStatusTypeList
                                                                .filter((statusObj) => {
                                                                    const isAlreadyUsed = rules.some(
                                                                        (r) => r.id !== rule.id && r.status === statusObj.StatusType
                                                                    );
                                                                    return !isAlreadyUsed;
                                                                })
                                                                .map((statusObj) => (
                                                                    <option
                                                                        key={statusObj.SystemAttendancePolicyStatusTypeId}
                                                                        value={statusObj.StatusType}
                                                                    >
                                                                        {statusObj.StatusType}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    ) : (
                                                        <span className={`badge px-3 py-1.5 fs-7 d-inline-flex align-items-center gap-2 bg-light-${currentColor} text-${currentColor}`} style={{ fontWeight: 600, borderRadius: '16px' }}>
                                                            <span className={`bg-${currentColor}`} style={{ width: 6, height: 6, borderRadius: '50%', display: 'inline-block' }} />
                                                            {currentStatusName}
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="text-start text-gray-700 fs-7 py-3.5 align-middle">
                                                    {editing && !isDisabled ? (
                                                        <select
                                                            value={rule.evaluationMethod}
                                                            onChange={(e) => updateRule(rule.id, { evaluationMethod: e.target.value })}
                                                            className="form-select form-select-sm fs-7 py-1 px-2 w-160px"
                                                        >
                                                            {evaluationMethodsList.map((method) => (
                                                                <option
                                                                    key={method.SystemAttendancePolicyEvaluationMethodId}
                                                                    value={method.EvaluationMethod}
                                                                >
                                                                    {method.EvaluationMethod}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span>{rule.evaluationMethod}</span>
                                                    )}
                                                </td>

                                                <td className="text-center text-gray-800 fw-bold fs-7 py-3.5 align-middle">
                                                    {editing && !isDefault && !isDisabled ? (
                                                        <select value={rule.operator} onChange={(e) => updateRule(rule.id, { operator: e.target.value })} className="form-select form-select-sm fs-7 py-1 px-2 mx-auto w-80px">
                                                            <option value=">=">&gt;=</option>
                                                            <option value=">">&gt;</option>
                                                            <option value="<=">&lt;=</option>
                                                            <option value="<">&lt;</option>
                                                            <option value="=">=</option>
                                                        </select>
                                                    ) : (
                                                        <span>{isDefault ? '-' : rule.operator}</span>
                                                    )}
                                                </td>

                                                <td className="text-start text-gray-700 fs-7 py-3.5 align-middle">
                                                    {editing && !isDefault && !isDisabled ? (
                                                        <input
                                                            type="number"
                                                            step="any" 
                                                            className="form-control form-control-sm fs-7 py-1 px-2 w-120px"
                                                            placeholder="0"
                                                            min="0"
                                                            value={rule.value} 
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                                    updateRule(rule.id, { value: val });
                                                                }
                                                            }}
                                                        />
                                                        // <input
                                                        //     type="number"
                                                        //     className="form-control form-control-sm fs-7 py-1 px-2 w-120px"
                                                        //     placeholder="0"
                                                        //     min="0"
                                                        //     value={String(rule.value).replace(/[^0-9]/g, '')}
                                                        //     onChange={(e) => {
                                                        //         const val = e.target.value;
                                                        //         if (val === '' || /^\d+$/.test(val)) {
                                                        //             updateRule(rule.id, { value: val });
                                                        //         }
                                                        //     }}
                                                        // />
                                                    ) : (
                                                        <span>{isDefault ? '-' : (rule.value.includes('Hours') ? rule.value : `${rule.value} Hours`)}</span>
                                                    )}
                                                </td>

                                                <td className="text-end py-3.5 align-middle">
                                                    <div className="d-flex align-items-center justify-content-end gap-1">
                                                        {!isDisabled && (
                                                            <>
                                                                <button type="button" onClick={() => setEditingId(editing ? null : rule.id)} className="btn btn-sm btn-icon bg-transparent border-0" style={{ width: '30px', height: '30px' }}>
                                                                    <i className={`bi ${editing ? 'bi-check-lg text-success fs-4' : 'bi-pencil text-gray-600 fs-7'}`}></i>
                                                                </button>
                                                                <button type="button" onClick={() => deleteRule(rule.id)} className="btn btn-sm btn-icon bg-transparent border-0" style={{ width: '30px', height: '30px' }}>
                                                                    <i className="bi bi-trash3 text-gray-500 fs-7"></i>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="policycard mb-10" style={{ pointerEvents: isDisabled ? 'none' : 'auto', opacity: isDisabled ? 0.75 : 1 }}>
                        <div className="row">
                            <div className="col-12 col-md-6">
                                <label className="fs-7 fw-semibold text-gray-700 mb-2">{getValueFromResource('Late Arrival Threshold (Minuntes)', dispatch, t)} <span className="text-danger">*</span></label>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        disabled={isDisabled}
                                        className={`form-control fs-7 h-40px input-bg ${errors.lateArrival ? 'border-danger' : ''}`}
                                        value={lateArrivalMinutes ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setLateArrivalMinutes(val === '' ? undefined : Number(val));
                                            validateField("lateArrival", val);
                                        }}
                                        onBlur={(e) => validateField("lateArrival", e.target.value)}
                                    />
                                </div>
                                {errors.lateArrival && <div className="text-danger fs-8 mt-1 fw-medium">{errors.lateArrival}</div>}
                            </div>

                            <div className="col-12 col-md-6">
                                <label className="fs-7 fw-semibold text-gray-700 mb-2">{getValueFromResource('Early Departure Threshold (Minuntes)', dispatch, t)} <span className="text-danger">*</span></label>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        disabled={isDisabled}
                                        className={`form-control fs-7 h-40px input-bg ${errors.earlyDeparture ? 'border-danger' : ''}`}
                                        value={earlyDepartureMinutes ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setEarlyDepartureMinutes(val === '' ? undefined : Number(val));
                                            validateField("earlyDeparture", val);
                                        }}
                                        onBlur={(e) => validateField("earlyDeparture", e.target.value)}
                                    />
                                </div>
                                {errors.earlyDeparture && <div className="text-danger fs-8 mt-1 fw-medium">{errors.earlyDeparture}</div>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-xl-3 d-flex flex-column gap-6">
                  
                    <div className="policycard" style={{ padding: '25px' }}>
                        <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
                            <h5 className="fw-bold mb-0 fs-7 text-dark">{getValueFromResource('Policy Summary', dispatch, t)}</h5>
                            <span className={`badge ${statusBadge.className} border rounded-pill px-2 py-0.5 fs-7 fw-semibold`} style={{ marginBottom: '-7px' }}>
                                {statusBadge.label}
                            </span>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            <div className="d-flex align-items-center gap-3 p-3 border border-dashed rounded policycard" style={{ backgroundColor: '#f9fafb', borderRadius: '20px' }}>
                                <div className="p-2 bg-primary-subtle rounded text-primary d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                                    <img src={EmployeeAssigned} alt="" />
                                </div>
                                <div>
                                    <span className="text-muted fs-8 d-block mb-1">Employees Assigned</span>
                                    <div className="fw-bold fs-6 text-gray-800 lh-1">
                                        {assignedEmployeesCount > 0 ? assignedEmployeesCount : '0'}
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-3 p-3 border border-dashed rounded policycard" style={{ backgroundColor: '#f9fafb', borderRadius: '20px' }}>
                                <div className="p-2 bg-danger-subtle rounded text-danger d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                                    <img src={DepartmentsAssigned} alt="" />
                                </div>
                                <div>
                                    <span className="text-muted fs-8 d-block mb-1">Departments Assigned</span>
                                    <div className="fw-bold fs-6 text-gray-800 lh-1">
                                        {assignedDepartmentsCount > 0 ? assignedDepartmentsCount : '0'}
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-3 p-3 border border-dashed rounded policycard" style={{ backgroundColor: '#f9fafb', borderRadius: '20px' }}>
                                <div className="p-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, backgroundColor: '#f1e7ff' }}>
                                   <img src={LocationAssigned} alt="" />
                                </div>
                                <div>
                                    <span className="text-muted fs-8 d-block mb-1">Locations Assigned</span>
                                    <div className="fw-bold fs-6 text-gray-800 lh-1">
                                        {assignedlocationsCount > 0 ? assignedlocationsCount : '0'}
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-3 p-3 border border-dashed rounded policycard" style={{ backgroundColor: '#f9fafb', borderRadius: '20px' }}>
                                <div className="p-2 bg-success-subtle rounded text-success d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                                    <img src={LateArrivalThreshold} alt="" />
                                </div>
                                <div>
                                    <span className="text-muted fs-8 d-block mb-1">Late Arrival Threshold </span>
                                    <div className="fw-bold fs-6 text-gray-800 lh-1">
                                        {lateArrivalMinutes !== undefined && lateArrivalMinutes !== null ? `${lateArrivalMinutes} Min` : '-'}
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-3 p-3 border border-dashed rounded policycard" style={{ backgroundColor: '#f9fafb', borderRadius: '20px' }}>
                                <div className="p-2 bg-warning-subtle rounded text-warning d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                                    <img src={EarlyDepartureThreshold} alt="" />
                                </div>
                                <div>
                                    <span className="text-muted fs-8 d-block mb-1">Early Departure Threshold</span>
                                    <div className="fw-bold fs-6 text-gray-800 lh-1">
                                        {earlyDepartureMinutes !== undefined && earlyDepartureMinutes !== null ? `${earlyDepartureMinutes} Min` : '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card policycard">
                        <div className="card-body p-5">
                            <h5 className="fw-bold border-bottom pb-3 mb-4 fs-7 text-dark">{getValueFromResource('Metadata', dispatch, t)}</h5>
                            <div className="d-flex flex-column gap-5">
                                <div className="w-100">
                                    <div className="text-muted fs-8 fw-medium mb-2" style={{ display: 'grid', gridTemplateColumns: '45% 10% 45%', alignItems: 'center' }}>
                                        <div className="d-flex align-items-center">
                                            <i className="ki-duotone ki-profile-circle text-muted fs-6 me-2">
                                                <span className="path1"></span><span className="path2"></span><span className="path3"></span>
                                            </i>
                                            <span>Last Modified By</span>
                                        </div>
                                        <div className="text-center text-muted fs-7">|</div>
                                        <div className="d-flex align-items-center">
                                            <i className="ki-duotone ki-calendar text-muted fs-6 me-2">
                                                <span className="path1"></span><span className="path2"></span>
                                            </i>
                                            <span>Modified Date</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '45% 10% 45%', alignItems: 'center' }}>
                                        <div className="fw-semibold fs-7 text-gray-800 text-truncate">
                                            {typeof modifiedByName !== 'undefined' ? modifiedByName : '-'}
                                        </div>
                                        <div></div>
                                        <div className="fw-semibold fs-7 text-gray-800 text-truncate">
                                            {modifiedDateStr || '-'}
                                        </div>
                                    </div>
                                </div>

                                <div className="border-top border-gray-200 my-1"></div>

                                <div className="w-100">
                                    <div className="text-muted fs-8 fw-medium mb-2" style={{ display: 'grid', gridTemplateColumns: '45% 10% 45%', alignItems: 'center' }}>
                                        <div className="d-flex align-items-center">
                                            <i className="ki-duotone ki-profile-circle text-muted fs-6 me-2">
                                                <span className="path1"></span><span className="path2"></span><span className="path3"></span>
                                            </i>
                                            <span>Created By</span>
                                        </div>
                                        <div className="text-center text-muted fs-7">|</div>
                                        <div className="d-flex align-items-center">
                                            <i className="ki-duotone ki-calendar text-muted fs-6 me-2">
                                                <span className="path1"></span><span className="path2"></span>
                                            </i>
                                            <span>Created Date</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '45% 10% 45%', alignItems: 'center' }}>
                                        <div className="fw-semibold fs-7 text-gray-800 text-truncate">
                                            {creatorName || '-'}
                                        </div>
                                        <div></div>
                                        <div className="fw-semibold fs-7 text-gray-800 text-truncate">
                                            {createdDateStr || '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AttendanceGuidelineModal 
                isOpen={guidelineModalOpen} 
                onClose={() => setGuidelineModalOpen(false)} 
            />
            <PolicyConflictModal 
                isOpen={modalOpen} 
                onClose={() => setModalOpen(false)} 
                data={conflictData} 
            />
        </div>
    );
};

export default AttendancePolicy;